import { memo, useMemo } from 'react';
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { BasemapCardGrid } from '@/components/ui/BasemapCardGrid';
import { BasemapSelect } from '@/components/ui/BasemapSelect';
import { getVisibleBasemaps } from '@/config/maplibre';
import type { BasemapId } from '@/config/maplibre';
import { ConfigSectionLabel } from './ConfigSectionLabel';

interface BasemapConfigProps {
  /** Selected country code — gates country-restricted basemap options. */
  countryCode: string | null;
  /** Currently selected default basemap. */
  defaultBasemapId: BasemapId;
  /** Basemaps included in the layers-menu allowlist. */
  allowedBasemapIds: BasemapId[];
  /** Called when the default basemap changes. */
  onDefaultChange: (id: BasemapId) => void;
  /** Called with the new allowlist (canonical order preserved). */
  onAllowedChange: (ids: BasemapId[]) => void;
}

/**
 * Picks the workspace default basemap (a thumbnail dropdown) plus the allowlist
 * of basemaps users may switch to in the layers menu (a thumbnail grid).
 * Country-restricted basemaps are only offered for matching countries; the
 * parent form keeps the default within the allowlist.
 */
export const BasemapConfig = memo(function BasemapConfig({
  countryCode,
  defaultBasemapId,
  allowedBasemapIds,
  onDefaultChange,
  onAllowedChange,
}: BasemapConfigProps) {
  const { t } = useTranslation();

  // Universe of selectable basemaps (all, with country-restricted ones gated).
  const availableBasemaps = useMemo(
    () => getVisibleBasemaps(null, countryCode),
    [countryCode]
  );

  // Default may only be chosen among the allowlisted basemaps.
  const defaultOptionIds = useMemo<BasemapId[]>(
    () =>
      availableBasemaps
        .filter((basemap) => allowedBasemapIds.includes(basemap.id))
        .map((basemap) => basemap.id),
    [availableBasemaps, allowedBasemapIds]
  );

  const availableItems = useMemo(
    () =>
      availableBasemaps.map((basemap) => ({
        id: basemap.id,
        labelKey: basemap.labelKey,
        thumbnail: basemap.thumbnail,
      })),
    [availableBasemaps]
  );

  // Toggle a single id in/out of the allowlist, re-deriving canonical order.
  const handleAllowedToggle = (id: BasemapId): void => {
    const next = new Set(allowedBasemapIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onAllowedChange(
      availableBasemaps
        .filter((basemap) => next.has(basemap.id))
        .map((basemap) => basemap.id)
    );
  };

  return (
    <Stack gap="md">
      {allowedBasemapIds.length > 0 && (
        <Stack gap="xs">
          <ConfigSectionLabel>
            {t('mapSetup.basemap.default')}
          </ConfigSectionLabel>
          <BasemapSelect
            options={defaultOptionIds}
            value={defaultBasemapId}
            onChange={onDefaultChange}
            label={t('mapSetup.basemap.default')}
          />
        </Stack>
      )}

      <Stack gap="xs">
        <ConfigSectionLabel>
          {t('mapSetup.basemap.availableInMenu')}
        </ConfigSectionLabel>
        <BasemapCardGrid
          mode="multi"
          items={availableItems}
          selectedIds={allowedBasemapIds}
          onToggle={handleAllowedToggle}
          disabled={availableItems.length === 1}
          disabledTooltipKey="layers.basemap.onlyAvailable"
        />
        {allowedBasemapIds.length === 0 && (
          <Text size="sm" c="red">
            {t('mapSetup.basemap.atLeastOne')}
          </Text>
        )}
      </Stack>
    </Stack>
  );
});
